import React, { useState } from "react";
import { login } from "@/lib/api";

export default function LoginGate({ onSuccess }) {
  const [p, setP] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Pre-warm audio + mic permission from the login click (a real user gesture).
  // Without this, Chrome / Safari refuse to open the mic prompt OR play any audio
  // because the page hasn't been "touched" recently.
  const primeBrowserPermissions = async () => {
    // 1. Unlock autoplay with a silent MP3 frame
    try {
      const a = new Audio("data:audio/mpeg;base64,SUQzAwAAAAAAFlRTU0UAAAAMAAADTGF2ZjU3LjcxLjEwMP/7kGQAAAAAAAAAAAAAAAAAAAAAAAAA");
      a.volume = 0;
      await a.play().catch(() => {});
    } catch {}
    // 2. Trigger the mic permission prompt while the user gesture is still hot
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(t => t.stop());
      }
    } catch (e) {
      console.warn("[Jarvis] mic prompt declined:", e?.name);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    // Fire permissions FIRST (still inside the user gesture from form submit)
    await primeBrowserPermissions();
    try {
      await login(p);
      onSuccess();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="login-gate">
      <div className="rc-panel relative p-8 w-full max-w-md">
        <div className="flex items-center gap-3 mb-3">
          <img
            src="/raw-data-logo.png"
            alt="RAW DATA"
            width={56}
            height={56}
            data-testid="login-brand-logo"
            style={{ display: "block", borderRadius: "50%" }}
          />
          <div>
            <div className="rc-label mb-1">// SECURE TERMINAL</div>
            <div className="font-display text-2xl rc-glow-text leading-none">RAW-DATA</div>
          </div>
        </div>
        <div className="text-xs text-[var(--rc-text-dim)] mb-6">Owner pairing required. Once paired, this device stays signed in for 12 months. Pairing will request microphone access — accept it so the assistant can hear you.</div>
        <div data-testid="wake-word-banner" className="rc-panel mb-6" style={{ padding: 12, background: "rgba(255,184,0,0.06)", border: "1px solid rgba(255,184,0,0.35)", borderRadius: 6 }}>
          <div className="rc-label mb-1" style={{ color: "var(--rc-gold)" }}>// YOUR WAKE WORD</div>
          <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: "0.15em", color: "var(--rc-gold)" }}>&quot;RAW COMPANY&quot;</div>
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>Say &quot;Raw Company&quot; anywhere in the app to wake the assistant, then give your command. Example: &quot;Raw Company, what&apos;s the weather?&quot;</div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <div className="rc-label mb-2 text-[10px]">PASSPHRASE</div>
            <input
              type="password"
              value={p}
              onChange={e => setP(e.target.value)}
              className="rc-input"
              autoFocus
              data-testid="login-passphrase"
              placeholder="• • • • • • • •"
            />
          </div>
          {err && <div className="text-[var(--rc-danger)] text-xs rc-fade-in" data-testid="login-error">{err}</div>}
          <button type="submit" disabled={busy || !p} className="rc-btn w-full !py-3" data-testid="login-submit">
            {busy ? "VERIFYING..." : "PAIR DEVICE"}
          </button>
        </form>

        <div className="rc-label text-[9px] mt-6 opacity-60">
          5 wrong attempts → 15-minute lockout. Voice listens continuously after pairing.
        </div>
      </div>
    </div>
  );
}
